import React from 'react';

import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';

import { useUpdateEmailDeliveryStatus } from '~/hooks/apis/customer-communication/useEmailDeliveryStatus';

import { InstructionBody } from '../DeliveryStatusCard/constants';

interface Props {
  body: InstructionBody;
  checkedSteps: Array<boolean>;
  onToggleStep: (index: number) => void;
  onRemove: () => void;
  onDismiss: () => void;
  mutation: ReturnType<typeof useUpdateEmailDeliveryStatus>;
}

export default function InstructionActionPanel({
  body,
  checkedSteps,
  onToggleStep,
  onRemove,
  onDismiss,
  mutation,
}: Props) {
  const allChecked = checkedSteps.length === body.instructions.length && checkedSteps.every(Boolean);

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: (theme) => `${theme.palette.error.main}08`,
        borderRadius: 1,
        border: 1,
        borderColor: (theme) => `${theme.palette.error.main}30`,
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {body.title}
      </Typography>

      <Stack sx={{ mb: 2 }}>
        {body.instructions.map((step, idx) => (
          <FormControlLabel
            key={idx}
            control={
              <Checkbox
                checked={checkedSteps[idx] ?? false}
                onChange={() => onToggleStep(idx)}
                size="small"
                sx={{ p: 0, mr: 1 }}
              />
            }
            label={step}
            sx={{
              alignItems: 'flex-start',
              marginRight: 0,
              '& .MuiFormControlLabel-label': { fontSize: '0.875rem', lineHeight: 1.5 },
            }}
          />
        ))}
      </Stack>

      {mutation.isSuccess && (
        <Alert severity="success" sx={{ borderRadius: 1, mb: 1 }}>
          Suppression status removed successfully. Changes might not be immediately visible.
        </Alert>
      )}

      {mutation.isError && (
        <Alert severity="error" sx={{ borderRadius: 1, mb: 1 }}>
          {mutation.error?.message || 'Something went wrong. Please create a support ticket.'}
        </Alert>
      )}

      {!mutation.isSuccess && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="contained" color="primary" disabled={!allChecked || mutation.isPending} onClick={onRemove}>
            {mutation.isPending ? 'Removing…' : body.removeButtonLabel}
          </Button>
          <Button variant="text" size="small" onClick={onDismiss}>
            Dismiss
          </Button>
        </Stack>
      )}
    </Box>
  );
}
